import React from 'react';
import { Table, Button, Space, Popconfirm, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { Link } from 'react-router-dom';
import { UserOutlined, EditOutlined, PlusOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { WebApiModelsView, WebApiModelsViewColumn, WebApiCustomListsDtoCustomListItemDto, WebApiModelsCustomListField, WebApiCustomListsDtoConnectedMemberDto } from '../../Repository/eventbookyapi';
import { UiControlType } from '../../Types/UiControlType';
import { sortByUiControlType, sortNumber } from '../../Helpers/TableSortHelper';
import { TextService } from '../../Services/TextService';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import { MemberStatusType } from '../../Types/MemberStatusType';

export interface CustomListTableProps {
    view: WebApiModelsView;
    items: WebApiCustomListsDtoCustomListItemDto[];
    customFields: WebApiModelsCustomListField[];
    lookupOptions: Record<string, any[]>;
    onEdit: (item: WebApiCustomListsDtoCustomListItemDto) => void;
    onDelete: (item: WebApiCustomListsDtoCustomListItemDto) => void;
    onManageMembers: (item: WebApiCustomListsDtoCustomListItemDto) => void;
}

export const CustomListTable = (props: CustomListTableProps) => {
    const GetText = TextService.GetText;

    // Sort records by column value
    const sortByColumn = (viewColumn: WebApiModelsViewColumn, a: WebApiCustomListsDtoCustomListItemDto, b: WebApiCustomListsDtoCustomListItemDto): number => {
        const aValue = getCellValue(viewColumn, a);
        const bValue = getCellValue(viewColumn, b);

        // Get the field to determine UI control
        const field = props.customFields.find(f => f.id === viewColumn.customFieldId);
        const uiControl = field?.uiControl;

        return sortByUiControlType(uiControl, aValue, bValue,
            uiControl === UiControlType.Lookup ? props.lookupOptions[viewColumn.customFieldId!] : undefined);
    };

    // Get raw cell value for sorting and rendering
    const getCellValue = (viewColumn: WebApiModelsViewColumn, record: any): any => {
        const customFieldId = viewColumn.customFieldId;
        if (!customFieldId) return null;

        // Access the flattened field value
        return record[customFieldId.toString()];
    };

    // Render cell value based on UI control type
    const renderCellValue = (viewColumn: WebApiModelsViewColumn, record: any): React.ReactNode => {
        const field = props.customFields.find(f => f.id === viewColumn.customFieldId);
        if (!field) return null;

        const value = getCellValue(viewColumn, record);

        switch (field.uiControl) {
            case UiControlType.YesNo:
                return value === true ? <CheckOutlined style={{ color: 'green' }} /> :
                    value === false ? <CloseOutlined style={{ color: 'red' }} /> : null;

            case UiControlType.Date:
                if (!value) return '';
                if (dayjs.isDayjs(value)) {
                    return value.isValid() ? value.format('YYYY-MM-DD') : '';
                }
                const d = dayjs(value);
                return d.isValid() ? d.format('YYYY-MM-DD') : '';

            case UiControlType.Lookup:
                const opts = props.lookupOptions[field.id!] || [];
                const found = opts.find((opt: any) => String(opt.value) === String(value));
                return found ? found.label : (value ?? "");

            case UiControlType.Number:
                return value != null ? Number(value).toLocaleString(getCurrentLanguage() === 'se' ? 'sv-SE' : 'en-US') : '';

            case UiControlType.Currency:
                return value != null ?
                    `${Number(value).toLocaleString(getCurrentLanguage() === 'se' ? 'sv-SE' : 'en-US',
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${GetText("Common_CurrencySymbol")}` : '';

            case UiControlType.Decimal:
                return value != null ?
                    Number(value).toLocaleString(getCurrentLanguage() === 'se' ? 'sv-SE' : 'en-US',
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';

            default:
                return value ?? '';
        }
    };

    // Build columns from view configuration
    const buildColumns = (): ColumnsType<any> => {
        const viewColumns = (props.view.viewColumns || [])
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        const columns: ColumnsType<any> = [
            // Always show Connected Members column first
            {
                title: <><UserOutlined /> {GetText("CustomList_ConnectedMembers") || "Connected Members"}</>,
                key: "connectedMembers",
                sorter: (a: any, b: any) => sortNumber((a.connectedMembers || []).length, (b.connectedMembers || []).length),
                render: (_: any, record: WebApiCustomListsDtoCustomListItemDto) => {
                    const connectedMembers = record.connectedMembers || [];
                    const hasMembers = connectedMembers.length > 0;
                    return (
                        <Space wrap align="center">
                            {hasMembers ? (
                                connectedMembers.map((member: WebApiCustomListsDtoConnectedMemberDto, index: number) => {
                                    const isCancelled = member.status === MemberStatusType.Cancelled;
                                    return (
                                        <Link key={index} to={`/${getCurrentLanguage()}/users/${member.memberId}`}>
                                            <Tag
                                                color={isCancelled ? "red" : "blue"}
                                                icon={<UserOutlined />}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {member.firstName} {member.lastName}
                                                {isCancelled && ` (${GetText("MemberList_StatusCancelled")})`}
                                            </Tag>
                                        </Link>
                                    );
                                })
                            ) : (
                                <span style={{ color: '#999' }}>-</span>
                            )}
                            {hasMembers ? (
                                <EditOutlined
                                    style={{ fontSize: '20px', color: '#1890ff', cursor: 'pointer' }}
                                    onClick={() => props.onManageMembers(record)}
                                />
                            ) : (
                                <PlusOutlined
                                    style={{ fontSize: '20px', color: '#1890ff', cursor: 'pointer' }}
                                    onClick={() => props.onManageMembers(record)}
                                />
                            )}
                        </Space>
                    );
                },
            }
        ];

        // Add columns from view
        viewColumns.forEach((viewColumn) => {
            const field = props.customFields.find(f => f.id === viewColumn.customFieldId);
            if (!field) return;

            let align: 'left' | 'right' | 'center' = 'left';
            if (field.uiControl === UiControlType.Number ||
                field.uiControl === UiControlType.Currency ||
                field.uiControl === UiControlType.Decimal) {
                align = 'right';
            }

            columns.push({
                title: field.nameAdmin,
                dataIndex: viewColumn.customFieldId?.toString(),
                key: viewColumn.customFieldId?.toString(),
                align: align,
                sorter: (a, b) => sortByColumn(viewColumn, a, b),
                render: (_: any, record: any) => renderCellValue(viewColumn, record)
            });
        });

        // Add Actions column at the end
        columns.push({
            title: GetText("MemberList_ActionColumn") || "Actions",
            key: "actions",
            render: (_: any, record: any) => (
                <Space>
                    <Button size="small" onClick={() => props.onEdit(record)}>
                        {GetText("Common_Edit") || "Edit"}
                    </Button>
                    <Popconfirm
                        title={GetText("Common_ConfirmDelete") || "Are you sure you want to delete?"}
                        onConfirm={() => props.onDelete(record)}
                        okText={GetText("Common_Yes") || "Yes"}
                        cancelText={GetText("Common_No") || "No"}
                    >
                        <Button size="small" danger>
                            {GetText("Common_Delete") || "Delete"}
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        });

        return columns;
    };

    return (
        <Table
            columns={buildColumns()}
            dataSource={props.items}
            rowKey={item => item.id!}
            scroll={{ x: 'max-content' }}
            pagination={false}
        />
    );
};

export default CustomListTable;
